import './bootstrap';
import './Plugins/navSource';
import '../css/app.css';
import 'primeicons/primeicons.css';
import 'katex/dist/katex.min.css';
import 'spatie-media-lib-pro/media-library-pro-styles/dist/styles.css';

import { createApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from 'ziggy-js';
import PrimeVue from 'primevue/config';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import Vapor from '../../node_modules/laravel-vapor/src/index.js';
import * as Sentry from '@sentry/vue';
import Tooltip from 'primevue/tooltip';
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import ConfirmDialog from 'primevue/confirmdialog';
import ConfirmationService from 'primevue/confirmationservice';

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'BV Portal';

Vapor.withBaseAssetUrl(import.meta.env.VITE_VAPOR_ASSET_URL);
window.Vapor = Vapor;

// Show loading overlay during form submissions (except chat). Partial visits
// (those that pass `only: [...]`) are treated as in-place updates and don't
// trigger the overlay — they preserve page state, so darkening the screen is
// disruptive.
router.on('start', (event) => {
    const visit = event.detail.visit;
    const url = visit.url?.toString() || '';
    const isPartial = Array.isArray(visit.only) && visit.only.length > 0;
    const isFormSubmission = visit.method !== 'get'
        && url !== window.location.href
        && !isPartial
        && !url.includes('/chat/')
        && !url.includes('/report/')
        && !url.includes('/user/settings');

    if (isFormSubmission) {
        document.body.style.cursor = 'wait';
        let overlay = document.getElementById('overlay');
        if (overlay) {
            overlay.style.display = 'block';
        }
    }
});

router.on('finish', () => {
    document.body.style.cursor = '';
    let overlay = document.getElementById('overlay');
    if (overlay) {
        overlay.style.display = 'none';
    }
});

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        // Initialize Sentry
        Sentry.init({
            app,
            dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
            environment: import.meta.env.VITE_APP_ENV,
            integrations: [
                // Sentry.replayIntegration()
            ],
            // Capture Replay for 10% of all sessions,
            // plus for 100% of sessions with an error
            // replaysSessionSampleRate: 0.1,
            // replaysOnErrorSampleRate: 1.0,
            beforeSend(event, hint) {
                if (event.exception) {
                    console.error('Error sent to Sentry:', hint.originalException || hint.syntheticException);
                }
                return event;
            },
        });

        app.config.errorHandler = (err, vm, info) => {
            Sentry.captureException(err);
            console.error(`Error: ${err.toString()}\nInfo: ${info}`);
        };

        app.directive('tooltip', Tooltip);
        app.component('Toast', Toast);
        app.component('ConfirmDialog', ConfirmDialog);

        app.use(plugin)
            .use(ZiggyVue, Ziggy)
            .use(PrimeVue, {
                csp: { nonce: document.querySelector('meta[name="csp-nonce"]').getAttribute('content') },
                zIndex: {
                    modal: 1100,
                    overlay: 1100,
                    menu: 1100,
                    tooltip: 1100,
                },
                theme: {
                    preset: definePreset(Aura, {
                        semantic: {
                            focusRing: {
                                width: '2px',
                                style: 'solid',
                                color: '{primary.color}',
                                offset: '2px',
                            },
                            primary: {
                                50: '{blue.50}',
                                100: '{blue.100}',
                                200: '{blue.200}',
                                300: '{blue.300}',
                                400: '{blue.400}',
                                500: '{blue.500}',
                                600: '{blue.600}',
                                700: '{blue.700}',
                                800: '{blue.800}',
                                900: '{blue.900}',
                                950: '{blue.950}',
                            },
                            formField: {
                                paddingX: '0.625rem',
                                paddingY: '0.5rem',
                                focusRing: {
                                    width: '0px',
                                    style: 'solid',
                                    color: '{primary.color}',
                                    offset: '0',
                                    shadow: 'none',
                                },
                            },
                            list: {
                                option: {
                                    padding: '0.375rem 0.625rem',
                                },
                            },
                        },
                        components: {
                            checkbox: {
                                root: {
                                    width: '0.875rem',
                                    height: '0.875rem',
                                    // Match the indigo-600 used by the
                                    // InfiniteScrollToggle (#4f46e5). For
                                    // Checkbox, Aura defines these tokens
                                    // directly under `root`.
                                    checkedBackground: '#4f46e5',
                                    checkedBorderColor: '#4f46e5',
                                    checkedHoverBackground: '#4338ca',
                                    checkedHoverBorderColor: '#4338ca',
                                    checkedFocusBorderColor: '#4338ca',
                                    focusRing: {
                                        color: '#4f46e5',
                                    },
                                },
                                icon: {
                                    size: '0.625rem',
                                },
                            },
                            toggleswitch: {
                                // Aura puts ToggleSwitch's checked colors
                                // under `colorScheme.light.root`, not at the
                                // component root — overriding the flat path
                                // silently does nothing.
                                root: {
                                    focusRing: {
                                        color: '#4f46e5',
                                    },
                                },
                                colorScheme: {
                                    light: {
                                        root: {
                                            checkedBackground: '#4f46e5',
                                            checkedHoverBackground: '#4338ca',
                                        },
                                    },
                                },
                            },
                            datatable: {
                                columnTitle: {
                                    fontWeight: '700',
                                },
                                headerCell: {
                                    padding: '0.5rem 0.75rem',
                                    color: '#000000',
                                    focusRing: {
                                        color: '#4f46e5',
                                    },
                                },
                                bodyCell: {
                                    padding: '0.5rem 0.75rem',
                                },
                                footerCell: {
                                    padding: '0.5rem 0.75rem',
                                },
                                paginatorBottom: {
                                    borderWidth: '0',
                                },
                            },
                            paginator: {
                                root: {
                                    borderRadius: '0',
                                    padding: '0.25rem 0.75rem',
                                    fontSize: '0.875rem',
                                },
                                navButton: {
                                    width: '2rem',
                                    height: '2rem',
                                },
                            },
                            multiselect: {
                                option: {
                                    padding: '0.375rem 0.625rem',
                                },
                            },
                        },
                    }),
                    options: {
                        darkModeSelector: false,
                    },
                },
            })
            .use(ToastService)
            .use(ConfirmationService)
            .mixin({
                methods: { asset: window.Vapor.asset },
            })
            .mount(el);

        return app;
    },
    progress: {
        includeCSS: false, // required for csp support - https://github.com/inertiajs/progress/issues/21
        color: '#0EA5E9',
    },
});
